import React from "react";
import styled from "styled-components";
import dayjs from "dayjs";

import MarkdownContent from "../../../components/markdown/MarkdownContent";
import Avatar from "../../../components/avatar/Avatar";
import DropdownMenu, {
  DropdownMenuItem,
} from "../../../components/dropdown-menu/DropdownMenu";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../components/date-display/DateDisplay";
import ContentTag from "../../../components/content-tag/ContentTag";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import ToolTip from "../../../components/tooltip/ToolTip";
import useCopyToClipboard from "../../../hooks/useCopyToClipboard";
import type { NoteDetail } from "../../../app/services/notes";
import { buildNoteLink, toMarkdownLink } from "../../../utils/deepLink";

/**
 * カード表示ノート（共有TIPS）の閲覧表示。
 *
 * ノート/カードはモデル統合済みで、これは display_style=card の
 * ノート（NoteDetail）用ビュー。NoteView と同じレイアウト（タイトル行
 * 右端の3点リーダーメニューに編集/リンクをコピー/削除、720px の読み物幅）。
 * メタ情報はグローバルコンポーネントで表現する:
 * - カテゴリ = ContentTag（補足情報のワード添え）
 * - 必須表示 / 非公開 / 期限切れ = StatusBadge（システム上の状態表示）
 * - 日付 = DateDisplay（ゼロ埋めなしの共通フォーマット）
 */

const ViewWrapper = styled.article`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  /* NoteView と同じく本文幅を 720px にし、左右の余白は画面が狭いときだけ取る */
  width: min(720px, 100% - 32px);
  margin: 0 auto;
  padding: 24px 0 96px;
`;

/* 枠付きボックスは utility に該当がないため局所 styled（レイアウトは u-row 系） */
const ReviewBox = styled.div.attrs({
  className: "u-row u-gap16 u-items-center",
})`
  padding: 12px 16px;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
`;

const ReviewStat = styled.span.attrs({
  className: "bodytext-13",
})`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--soremo-gray);

  .material-symbols-rounded {
    font-size: 16px;
  }
`;

export interface NoteCardViewProps {
  card: NoteDetail;
  onEdit?: () => void;
  onDelete?: () => void;
  /** ノート表示への切替（直接更新権限がある場合のみ渡す） */
  onSwitchStyle?: () => void;
  /** リンクコピーをプロジェクトスコープ URL（?tab=notes&note=）にする場合に渡す */
  projectId?: string;
  /**
   * 下書き（未提出の変更提案）の表示: タイトル横に「下書き」チップを出し、
   * 実体ページが無いためリンクコピーを隠す
   */
  draftMode?: boolean;
  /** 削除メニューのラベル差し替え（下書きでは「取り下げ」） */
  deleteLabel?: string;
}

const NoteCardView: React.FC<NoteCardViewProps> = ({
  card,
  onEdit,
  onDelete,
  onSwitchStyle,
  projectId,
  draftMode = false,
  deleteLabel = "削除",
}) => {
  const expired = card.expires_at
    ? dayjs(card.expires_at).isBefore(dayjs())
    : false;

  // タイトル行右端の3点リーダーメニュー
  // （編集/削除は権限に応じて表示、リンクコピーは閲覧できる人なら誰でも可）
  const { copy } = useCopyToClipboard({
    successMessage: "リンクをコピーしました",
  });
  const menuItems: DropdownMenuItem[] = [];
  if (onEdit) {
    menuItems.push({ label: "編集", icon: "edit", onClick: onEdit });
  }
  if (onSwitchStyle) {
    menuItems.push({
      label: "ノート表示に切り替え",
      icon: "description",
      onClick: onSwitchStyle,
    });
  }
  // 下書きは実体ページ（/notes/:id）が無いためリンクコピーは出さない
  if (!draftMode) {
    menuItems.push({
      label: "リンクをコピー",
      icon: "link",
      onClick: () => {
        void copy(
          toMarkdownLink(card.title, buildNoteLink(card.id, projectId)),
        );
      },
    });
  }
  if (onDelete) {
    menuItems.push({ label: deleteLabel, icon: "delete", onClick: onDelete });
  }

  return (
    <ViewWrapper>
      <div>
        <div className="u-row u-gap8 u-items-center u-mb8">
          {card.category && <ContentTag>{card.category}</ContentTag>}
          {card.is_required && (
            <StatusBadge label="必須表示" color="blue" size="small" />
          )}
          {!card.is_published && (
            <StatusBadge label="ホーム未投稿" size="small" />
          )}
          {card.expires_at && (
            <StatusBadge
              label={
                <span className="u-row u-gap4 u-items-center">
                  {expired ? "期限切れ" : "期限"}
                  <DateDisplay
                    date={card.expires_at}
                    type={DATE_DISPLAY_TYPE.DATE_ONLY}
                  />
                </span>
              }
              color={expired ? "blue" : "light-gray"}
              size="small"
            />
          )}
        </div>

        <div className="u-row-between u-gap8 u-mb8">
          <div className="u-row u-gap8 u-items-center">
            <h1 className="heading-spacing-21 u-line-height-150">
              {card.title}
            </h1>
            {draftMode && (
              <ToolTip description="未提出の変更提案です。プルリクエストを送信するまで他のメンバーには公開されません">
                <StatusBadge label="下書き" size="small" />
              </ToolTip>
            )}
          </div>
          <DropdownMenu items={menuItems} size="small" />
        </div>

        <div className="u-row u-gap24 u-items-center">
          <span className="bodytext-11 u-text-light-gray">
            {card.modified && (
              <DateDisplay
                date={card.modified}
                type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
              />
            )}
          </span>
          {card.created_by && (
            <div className="u-row u-gap8 u-items-center">
              <Avatar
                size={32}
                src={card.created_by.avatar || undefined}
                name={card.created_by.display_name}
                tooltipText={card.created_by.display_name}
              />
              <span className="bodytext-11 u-text-light-gray">
                {card.created_by.display_name}
              </span>
            </div>
          )}
        </div>
      </div>

      {card.review && (
        <ReviewBox>
          <span className="heading-13 u-text-light-gray">レビュー情報</span>
          <ReviewStat>
            <span className="material-symbols-rounded">check_circle</span>
            OK 済み {card.review.ok_count}人
          </ReviewStat>
          <ReviewStat>
            <span className="material-symbols-rounded">thumb_up</span>
            役に立った {card.review.helpful_count}件
          </ReviewStat>
        </ReviewBox>
      )}

      <MarkdownContent
        content={card.content}
        variant="document"
        breaks
        zoomableImages
        attachmentDisplay="preview-card"
        className="u-text-justify"
      />
    </ViewWrapper>
  );
};

export default NoteCardView;
